import React, { useEffect, useMemo, useRef } from 'react';
import * as echarts from 'echarts';
import FormCard from '../Components/Forms/FormCard';
import { chartColorPalette } from '../utils/utils';

const EMPTY_COLOR_TAG_ORDER = [];

const TimeSeriesChart = ({ chartData = {}, colorTagOrder }) => {
  const chartRef = useRef(null);
  const resolvedColorTagOrder =
    Array.isArray(colorTagOrder) ? colorTagOrder : EMPTY_COLOR_TAG_ORDER;
  const orderedChartData = useMemo(() => {
    const sourceKeys = Array.isArray(chartData.keys) ? chartData.keys : [];
    const sourceValues = Array.isArray(chartData.values) ? chartData.values : [];
    const preferredOrder = resolvedColorTagOrder;
    const originalIndexByTag = sourceKeys.reduce((acc, key, index) => {
      acc[key] = index;
      return acc;
    }, {});

    const orderedKeys = [
      ...preferredOrder.filter((tag) => originalIndexByTag[tag] !== undefined),
      ...sourceKeys.filter((tag) => !preferredOrder.includes(tag)),
    ];

    const valueSeriesByKey = orderedKeys.reduce((acc, key) => {
      const sourceIndex = originalIndexByTag[key];
      acc[key] = sourceValues.map((valueRow) => valueRow[sourceIndex]);
      return acc;
    }, {});

    return {
      keys: orderedKeys,
      timestamp: chartData.timestamp || [],
      valueSeriesByKey,
    };
  }, [chartData, resolvedColorTagOrder]);

  const createNewChart = () => {
    const chartOption = {
      title: {
        text: 'Tags Data',
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
        },
      },
      grid: {
        left: '10%',
        right: '10%',
        containLabel: true,
      },
      toolbox: {
        feature: {
          dataView: { show: true, readOnly: false },
          restore: { show: true },
          dataZoom: {
            yAxisIndex: 'none',
          },
          saveAsImage: {},
        },
      },
      legend: {
        data: orderedChartData.keys,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: orderedChartData.timestamp,
      },
      yAxis: [],
      dataZoom: [
        {
          type: 'slider',
        },
        {
          type: 'inside',
        },
      ],
      series: [],
    };

    if (orderedChartData.keys.length > 0) {
      const colorIndexByTag = resolvedColorTagOrder.reduce((acc, tag, idx) => {
        if (tag && acc[tag] === undefined) {
          acc[tag] = idx;
        }
        return acc;
      }, {});

      orderedChartData.keys.forEach((key, index) => {
        const paletteIndex = colorIndexByTag[key] !== undefined
          ? colorIndexByTag[key]
          : index;

        chartOption.series.push({
          name: key,
          type: 'line',
          yAxisIndex: index,
          symbol: 'none',
          sampling: 'lttb',
          data: orderedChartData.valueSeriesByKey[key] || [],
          lineStyle: { color: chartColorPalette[paletteIndex % chartColorPalette.length] },
          itemStyle: { color: chartColorPalette[paletteIndex % chartColorPalette.length] },
        });



        chartOption.yAxis.push({
          type: 'value',
          name: key,
          position: index % 2 === 0 ? 'left' : 'right', // placing y-axis alternately on left and right
          offset: Math.floor(index / 2) * 70, // Increased offset for more space
          alignTicks: true, // Align ticks for better readability
          nameLocation: 'middle', //position y-axis name in the middle
          nameGap: 30, // Increase gap between y-axis and name
          nameRotate: 90, // Rotate y-axis name vertically
          axisLine: {
            show: true,
          },
          axisLabel: {
            formatter: '{value}',
            rotate: 45
          },
        });
      });
    }

    return chartOption;
  };

  useEffect(() => {
    const chartInstance = echarts.init(chartRef.current);
    const chartOption = createNewChart();
    chartInstance.setOption(chartOption);

    // Make chart responsive
    const handleResize = () => {
      chartInstance.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.dispose();
    };
  }, [orderedChartData, resolvedColorTagOrder]);

  return <FormCard>
    <div ref={chartRef} style={{ height: '100%', minHeight: '400px', width: '100%' }} />
  </FormCard>;
};

export default TimeSeriesChart;

